import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";

import { axiosGetRequest, axiosPutRequest } from "../api/ApiClient";
import AssociationComponent from "../components/AssociationComponent";
import Autocomplete from "../components/Autocomplete";
import DialogMessage from "../components/DialogMessage";
import OptionsSelect from "../model/OptionsSelect";
import {
    StyledBody,
    StyledHeader,
    StyledLabel,
    StyledSubTitle,
    StyledTitle,
} from "../styles/MainStyles";
import GenericUtils from "../utils/GenericUtils";
import StringUtils from "../utils/types/StringUtils";
import { IWorkflowType } from "../context/interfaces/IWorkflowContext";
import { User } from "../model/dto/User";
import { GenericValues } from "../model/dto/GenericValues";
import UserService from "../service/Docente/UserService";
import UsersDTO from "../model/dto/UsersDTO";
import { WorkflowTypeEnum } from "../model/Enums/WorkflowTypeEnum";

function ManagePermission() {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();

    const translate = {
        operationSuccess: StringUtils.translate("operationSuccess"),
        operationFailure: StringUtils.translate("operationFailure"),
        title: StringUtils.translate("managePermissionTitle"),
        subtitle: StringUtils.translate("managePermissionSubtitle"),
        label: StringUtils.translate("managePermissionLabel"),
        placeholder: StringUtils.translate("managePermissionPlaceholder"),
        selectUser: StringUtils.translate("managePermissionSelectUser"),
        leftTableTitle: StringUtils.translate("managePermissionLeftTableTitle"),
        rigthTableTitle: StringUtils.translate(
            "managePermissionRigthTableTitle"
        ),
    };

    const [workflows, setWorkflows] = useState<any[]>([]);
    const [workflowsDropdown, setWorkflowsDropdown] = useState<OptionsSelect[]>(
        []
    );
    const [workflowSelected, setWorkflowSelected] = useState<any[]>([]);
    const [workFlowSelectedId, setWorkFlowSelectedId] = useState("");
    const [users, setUsers] = useState<any[]>([]);
    const [usersLinked, setUsersLinked] = useState<any[]>([]);
    // Validation states
    const [, setAddRemove] = useState("");
    const [isSave, setIsSave] = useState(false);
    // Dialog states
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const getWorkflows = async () => {
        const response = await axiosGetRequest(
            "/docente/v1/workflows",
            acessToken,
            instance
        );

        if (GenericUtils.isSuccessStatus(response.status) && response.data) {
            setWorkflowsDropdown(
                response.data
                    .filter(
                        (workflowType: IWorkflowType) =>
                            workflowType.workflowId !==
                            WorkflowTypeEnum.HoraExtra
                    )
                    .map((workflowType: IWorkflowType) => {
                        // response.data.map((workflowType: IWorkflowType) => {
                        return {
                            value: workflowType.workflowId,
                            label: workflowType.nome,
                        };
                    })
            );
            setWorkflows(
                response.data.map((element: any) => {
                    return {
                        workflowId: element.workflowId,
                        nome: element.nome,
                        listaUsuariosAprovadores:
                            element.listaUsuariosAprovadores.map(
                                (user: any) => {
                                    return {
                                        docenteID: user.docenteId,
                                        nome: user.nome,
                                        matriculaAdp: String(user.matricula),
                                        perfis: user.perfis,
                                    };
                                }
                            ),
                    };
                })
            );
        }
    };

    async function getUsers() {
        // const response = await axiosGetRequest(
        //     "/docente/v1/usuarios?_limit=99999&perfis=1",
        //     acessToken,
        //     instance
        // );

        const dto = {
            _limit: "5000",
            _offset: "1",
            perfis: "1",
        } as UsersDTO;

        const perfisQuery = "&query=listaPerfis(idPerfil%20in%20(1))";

        const response = await UserService.getUsers(dto, perfisQuery, instance);

        if (response.status === 200 && response?.data) {
            const result = response.data as GenericValues<User>;

            setUsers(
                result.items.map((element: User) => {
                    return {
                        docenteID: element.docenteId,
                        nome: element.nome,
                        matriculaAdp: element.matricula,
                        perfis: element.perfis,
                    };
                })
            );
        }
    }

    async function putUsersLinked(params: any[]) {
        const body = params.map((element) => {
            return {
                idUsuario: element.docenteID,
            };
        });

        const response = await axiosPutRequest(
            `/docente/v1/workflows/${workFlowSelectedId}/vinculo-usuario`,
            body,
            acessToken,
            instance
        );

        if (GenericUtils.isSuccessStatus(response.status)) {
            setSuccessMessage(translate.operationSuccess);
            setIsSave(false);
        } else {
            setErrorMessage(
                response?.data?.details || translate.operationFailure
            );
        }
    }

    const handlePutUsersLinked = (params: any[]) => {
        putUsersLinked(params);
    };

    const handleCloseDialogSuccess = () => {
        setSuccessMessage("");
        getWorkflows();
    };

    const handleCloseDialogError = () => {
        setErrorMessage("");
    };

    useEffect(() => {
        getWorkflows();
        getUsers();
    }, []);

    useEffect(() => {
        if (workflowSelected.length > 0) {
            setWorkFlowSelectedId(workflowSelected[0].value);
        } else {
            setWorkFlowSelectedId("");
        }
    }, [workflowSelected]);

    useEffect(() => {
        if (workFlowSelectedId) {
            setUsersLinked(
                workflows.find(
                    (item) => item.workflowId === workflowSelected[0].value
                )?.listaUsuariosAprovadores
            );
        } else {
            setUsersLinked([]);
        }
    }, [workFlowSelectedId]);

    const DialogSuccess = (
        <DialogMessage
            type="success"
            show={Boolean(successMessage)}
            title={successMessage}
            onClose={handleCloseDialogSuccess}
        />
    );

    const DialogError = (
        <DialogMessage
            type="error"
            show={Boolean(errorMessage)}
            title={errorMessage}
            onClose={handleCloseDialogError}
        />
    );

    return (
        <>
            <StyledHeader>
                <StyledTitle>{translate.title}</StyledTitle>
                <StyledSubTitle>{translate.subtitle}</StyledSubTitle>
            </StyledHeader>
            <StyledBody>
                <StyledLabel>{translate.label}</StyledLabel>
                <div style={{ display: "flex" }}>
                    <Autocomplete
                        placeholder={translate.placeholder}
                        options={workflowsDropdown}
                        selected={workflowSelected}
                        onChange={setWorkflowSelected}
                    />
                </div>
                {workFlowSelectedId !== "" ? (
                    <>
                        <StyledLabel>{translate.selectUser}</StyledLabel>
                        <AssociationComponent
                            title={translate.leftTableTitle}
                            titleSecond={translate.rigthTableTitle}
                            association={users}
                            linked={usersLinked}
                            onClick={handlePutUsersLinked}
                            setAddRemove={setAddRemove}
                            isSave={isSave}
                            setIsSave={setIsSave}
                        />
                    </>
                ) : null}
            </StyledBody>
            {successMessage ? DialogSuccess : null}
            {errorMessage ? DialogError : null}
        </>
    );
}

export default ManagePermission;
